import styled from "styled-components";

import {
  dateFormatted,
  getTimeAMPM,
  numberWithCommas,
} from "modules/functions";
import * as css from "styles/CssVariables.js";
import globalConstants from "constants/globalConstants";

import dollarCashBonusIcon from "assets/img/icons/dollar-icon-cash-bonus.svg";
import padlockIcon from "assets/img/icons/padlock-icon.svg";
import padlockGreyIcon from "assets/img/icons/padlock-icon-grey.svg";
import referralsQualifiedGreyIcon from "assets/img/icons/referral-qualified-icon-grey.svg";
import referralsQualifiedIcon from "assets/img/icons/referral-qualified-icon.svg";
import cashGreenIcon from "assets/img/icons/cash-icon-green.svg";

const CashBonus = styled.div`
  position: absolute;
  top: 36px;
  left: 0;
  width: 100%;
  box-sizing: border-box;

  margin: 0;
  padding: 0 7px;

  display: flex;
  flex-direction: column;

  opacity: ${(props) => (props.display ? 100 : 0)};

  transition: 0.2s;
`;

const CashBonusBox = styled.div`
  width: 100%;
  padding: 10px 10px 15px;
  box-sizing: border-box;
  margin-top: 8px;

  background: ${css.BLUE_BOX_GRADIENT};
  border-radius: 10px;

  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const BoxTitle = styled.div`
  width: 100%;

  line-height: 130%;

  font-size: 13px;
  font-weight: ${css.BOX_TITLE_FONT_WEIGHT};
`;

const DollarIcon = styled.div`
  display: inline-block;
  padding-right: 3px;

  transform: translateY(4.5px);

  width: 20px;
  min-width: 20px;
`;

const Text = styled.div`
  line-height: 125%;
  font-size: 11px;
`;

const CashBonusSummary = styled.div`
  width: 100%;
  padding: 15px 8px 15px 12px;
  margin-top: 8px;
  box-sizing: border-box;

  background: ${css.FLAT_BOX_BACKGROUND};
  border-radius: ${css.BOX_RADIUS_SECONDARY};

  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const MonthBox = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Month = styled.div`
  font-size: 13px;
`;

const Badge = styled.div`
  height: 16px;
  padding: 0 9px;

  font-size: 10px;

  border-radius: 11px;
  background: ${css.POSITIVE_GREEN};

  display: flex;
  justify-content: center;
  align-items: center;
`;

const BarLines = styled.div`
  width: 100%;

  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 1px;
`;

const BarLine = styled.div`
  width: 100%;

  color: ${(props) => (props.grey ? "#9F9F9F" : "#fff")};

  display: flex;
  align-items: center;
  gap: 6px;
`;

const BonusBox = styled.div`
  min-width: 70px;

  display: flex;
  align-items: center;
  gap: 5px;
`;

const CounterBox = styled.div`
  min-width: 48px;

  display: flex;
  align-items: center;
  gap: 5px;

  @media (max-width: ${globalConstants.switchMobile}) {
    min-width: 58px;
  }
`;

const IconTextBoxIcon = styled.div`
  width: 12px;
  min-width: 12px;
`;

const IconTextBoxText = styled.div`
  font-size: 11px;
  font-weight: 500;
  color: ${(props) => (props.green ? css.BRAND_GREEN : "inherit")};

  display: flex;
  align-items: center;
  gap: 5px;

  @media (max-width: ${globalConstants.switchMobile}) {
    font-size: 14px;
  }
`;

const Bar = styled.div`
  position: relative;
  height: 14px;

  overflow: hidden;

  border-radius: 10px;
  background: ${(props) => (props.grey ? "#617489" : css.BRAND_INDIGO)};

  flex: 1;
`;

const BarProgress = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: ${(props) => props.progresspercent}%;
  box-sizing: border-box;

  background: ${css.BRAND_GREEN};
`;

const ButtonWrap = styled.div`
  margin-top: 8px;

  display: flex;
`;

const ButtonSmall = styled.div`
  box-sizing: border-box;
  height: 18px;
  padding: 0 10px;

  background: ${css.BRAND_YELLOW};
  border-radius: 14px;
  border: 0;

  font-size: 9px;
  font-weight: 400;
  color: ${css.DARK_BLUE};
  white-space: nowrap;
  line-height: 1;

  transition: 0.2s;

  display: flex;
  align-items: center;
`;

const CashBonusSmallInfo = styled.div`
  margin-top: 8px;

  font-size: 8px;
  color: ${css.TEXT_GREY};
  line-height: 125%;
`;

export default ({ display }) => {
  const bonusLevels = [10, 30, 70, 150, 310, 630];
  const bonusAmounts = [1, 2, 5, 15, 60, 320];

  const thisMonthQualified = 218;

  let activeLevel = 1;

  for (let i = 0; i <= bonusLevels.length; i++) {
    if (thisMonthQualified >= bonusLevels[i]) {
      activeLevel++;
    }
  }

  const barLines = bonusAmounts.map((amount, index) => {
    const levelStart = index > 0 ? bonusLevels[index - 1] : 0;

    const levelReferrals =
      index > 0 ? bonusLevels[index] - levelStart : bonusLevels[index];

    const lineNumber = index + 1;

    const greyed = lineNumber > activeLevel;

    let progress = 0;
    let progressPercent = 0;
    let unlocked = false;

    if (lineNumber < activeLevel) {
      unlocked = true;
      progress = levelReferrals;
      progressPercent = 100;
    }

    if (lineNumber === activeLevel) {
      progress = thisMonthQualified - levelStart;
      progressPercent =
        (100 / (bonusLevels[index] - levelStart)) *
        (thisMonthQualified - levelStart);
    }

    return (
      <BarLine grey={greyed ? 1 : 0} key={lineNumber}>
        <BonusBox>
          <IconTextBoxIcon>
            {!unlocked && (
              <img
                src={greyed ? padlockGreyIcon : padlockIcon}
                style={{ width: "100%" }}
              />
            )}
            {unlocked && (
              <img
                src={cashGreenIcon}
                style={{ width: "100%", paddingTop: "1px" }}
              />
            )}
          </IconTextBoxIcon>
          <IconTextBoxText green={unlocked ? 1 : 0}>
            ${amount} Bonus
          </IconTextBoxText>
        </BonusBox>
        <CounterBox>
          <IconTextBoxIcon>
            <img
              src={greyed ? referralsQualifiedGreyIcon : referralsQualifiedIcon}
              style={{ width: "100%" }}
            />
          </IconTextBoxIcon>
          <IconTextBoxText>
            {progress}/{levelReferrals}
          </IconTextBoxText>
        </CounterBox>
        <Bar grey={greyed ? 1 : 0}>
          <BarProgress progresspercent={progressPercent} />
        </Bar>
      </BarLine>
    );
  });

  return (
    <CashBonus display={display}>
      <CashBonusBox>
        <BoxTitle>
          <DollarIcon>
            <img src={dollarCashBonusIcon} style={{ width: "100%" }} />
          </DollarIcon>
          Earn With Referral Program
        </BoxTitle>
        <Text>
          Refer enough users in one month and receive a generous bonus. Unlock
          bonuses one by one and earn more than $400 per month.
        </Text>
        <CashBonusSummary>
          <MonthBox>
            <Month>April 2025</Month>
            <Badge>In progress</Badge>
          </MonthBox>
          <BarLines>{barLines}</BarLines>
        </CashBonusSummary>
        <ButtonWrap>
          <ButtonSmall>Payout Setup</ButtonSmall>
        </ButtonWrap>
        <CashBonusSmallInfo>
          * Only qualified referrals count towards the calculation of the cash
          bonus. A user must find a total of at least 1,000 coins while browsing
          posts on the Discover page to become a qualified referral.
        </CashBonusSmallInfo>
      </CashBonusBox>
    </CashBonus>
  );
};
